import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  ApiError,
  getTenantById,
  updateTenant,
  type TenantPayload,
} from "../../inquilinos/inquilinosApi";
import { APP_LOCALE } from "../../config/locale";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para editar inquilinos.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível atualizar o inquilino.";
};

const parseLoadError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar inquilinos.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar o inquilino.";
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

export function EditarInquilinoView() {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const [nomeInquilino, setNomeInquilino] = useState("");
  const [nif, setNif] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [iban, setIban] = useState("");
  const [fiador, setFiador] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState<string | null>(null);
  const [tenantSnapshotId, setTenantSnapshotId] = useState<number | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadTenant = async () => {
      if (!tenantId) {
        setLoadError("Inquilino inválido.");
        setIsLoading(false);
        return;
      }

      const parsedId = Number(tenantId);
      if (Number.isNaN(parsedId)) {
        setLoadError("Inquilino inválido.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      setSubmitError(null);

      try {
        const response = await getTenantById(parsedId);
        if (!isMounted) return;

        setTenantSnapshotId(response.id);
        setNomeInquilino(response.name ?? "");
        setNif(response.taxId ?? "");
        setEmail(response.email ?? "");
        setTelefone(response.phoneNumber ?? "");
        setIban(response.iban ?? "");
        setFiador(response.guarantor ?? "");
        setLastUpdatedLabel(formatDate(response.lastModified));
      } catch (error) {
        if (!isMounted) return;
        setLoadError(parseLoadError(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadTenant();

    return () => {
      isMounted = false;
    };
  }, [tenantId]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!tenantSnapshotId) {
      setSubmitError("Inquilino inválido.");
      return;
    }

    if (!nomeInquilino.trim()) {
      setSubmitError("O nome do inquilino é obrigatório.");
      return;
    }

    setIsSaving(true);

    const payload: TenantPayload = {
      name: nomeInquilino.trim(),
      taxId: nif.trim() || null,
      email: email.trim() || null,
      phoneNumber: telefone.trim() || null,
      iban: iban.trim() || null,
      guarantor: fiador.trim() || null,
    };

    try {
      await updateTenant(tenantSnapshotId, payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    navigate("/inquilinos", {
      state: { toast: "Inquilino atualizado com sucesso" },
    });
  };

  const actions = (
    <div className="flex items-center gap-4">
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/inquilinos")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "editar-inquilino-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving || isLoading}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Alterações"}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Editar Inquilino"
        subtitle="Atualize os dados do inquilino."
        actions={actions}
        backButton={{ onClick: () => navigate("/inquilinos") }}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {loadError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {loadError}
            </div>
          )}

          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <div
            className="text-secondary-text mb-6"
            style={{
              fontSize: "var(--text-caption)",
              fontWeight: "var(--font-weight-normal)",
            }}
          >
            Última atualização: {lastUpdatedLabel ?? "—"}
          </div>

          <form id="editar-inquilino-form" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Dados do Inquilino
                  </h3>

                  <div>
                    <label
                      htmlFor="nomeInquilino"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Nome do Inquilino{" "}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      id="nomeInquilino"
                      type="text"
                      value={nomeInquilino}
                      onChange={(event) =>
                        setNomeInquilino(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="nif"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      NIF
                    </label>
                    <input
                      id="nif"
                      type="text"
                      value={nif}
                      onChange={(event) => setNif(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="telefone"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Telefone
                    </label>
                    <input
                      id="telefone"
                      type="tel"
                      value={telefone}
                      onChange={(event) =>
                        setTelefone(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Dados Financeiros
                  </h3>

                  <div>
                    <label
                      htmlFor="iban"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      IBAN
                    </label>
                    <input
                      id="iban"
                      type="text"
                      value={iban}
                      onChange={(event) => setIban(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="fiador"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Fiador
                    </label>
                    <input
                      id="fiador"
                      type="text"
                      value={fiador}
                      onChange={(event) => setFiador(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
